<template>
  <v-dialog
    :value="open"
    max-width="1100"
    persistent
    content-class="orden-show-dialog-content"
    @input="$emit('close')"
  >
    <v-card v-if="orden" class="orden-show-card">
      <v-card-title>
        Orden de Venta
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="orden-show-content pb-2">

        <!-- ── INFO PANEL ── -->
        <div class="show-info-panel mb-3">
          <v-row dense>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-account</v-icon>Cliente</div>
                <div class="info-value">{{ orden.cliente ? orden.cliente.razonSocial : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-account-tie</v-icon>Vendedor</div>
                <div class="info-value">{{ vendedorDisplay(orden) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-warehouse</v-icon>Depósito</div>
                <div class="info-value">{{ orden.deposito ? orden.deposito.nombre : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-calendar</v-icon>Fecha</div>
                <div class="info-value">{{ formatFecha(orden.fecha || orden.createdAt) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-format-list-numbered</v-icon>Lista de precios</div>
                <div class="info-value">{{ orden.listaPreciosVentaNombre || "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label">Estado</div>
                <div class="mt-1">
                  <v-chip small dark class="white--text" :color="estadoColor(orden.estado)">{{ labelEstadoOrdenVenta(orden.estado) }}</v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label">Estado pago</div>
                <div class="mt-1">
                  <v-chip small dark class="white--text" :color="orden.estadoPago === 'PAGADO' ? 'success' : orden.estadoPago === 'PAGO_PARCIAL' ? 'orange' : 'error'">{{ labelEstadoPago(orden.estadoPago) }}</v-chip>
                </div>
              </div>
            </v-col>
            <v-col v-if="orden.estadoPago === 'PAGADO' && orden.fechaPagoCompletado" cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-cash-check</v-icon>Fecha pago total</div>
                <div class="info-value">{{ formatFecha(orden.fechaPagoCompletado) }}</div>
              </div>
            </v-col>
            <v-col v-if="orden.estadoPago === 'PAGADO' && orden.montoComisionVendedor != null" cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-account-cash</v-icon>Comisión vendedor</div>
                <div class="info-value success--text font-weight-medium">${{ Number(orden.montoComisionVendedor).toFixed(2) }}</div>
              </div>
            </v-col>
            <v-col v-if="orden.totalPagado > 0" cols="6" sm="3">
              <div class="info-field">
                <div class="info-label">Ya pagado</div>
                <div class="info-value success--text">${{ Number(orden.totalPagado || 0).toFixed(2) }}</div>
              </div>
            </v-col>
            <v-col v-if="restoAPagar > 0" cols="6" sm="3">
              <div class="info-field">
                <div class="info-label">Resto a pagar</div>
                <div class="info-value error--text font-weight-bold">${{ restoAPagarFormatted }}</div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- ── TABLA ── -->
        <div class="table-fixed-header-wrap">
          <v-data-table
            class="mt-2 movimiento-show-table"
            fixed-header
            height="380"
            :headers="headersItems"
            :items="orden.items || []"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.nombre="{ item }">{{ getProductoNombre(item) }}</template>
            <template v-slot:item.marca="{ item }">{{ item.productoMarca || "-" }}</template>
            <template v-slot:item.modelo="{ item }">{{ item.productoModelo || "-" }}</template>
            <template v-slot:item.tamanio="{ item }">{{ item.productoTamanio || "-" }}</template>
            <template v-slot:item.cantidad="{ item }">{{ item.cantidad }}</template>
            <template v-slot:item.precioUnitario="{ item }">
              <span class="d-inline-flex align-center">
                ${{ getPrecioUnitarioOriginal(item).toFixed(2) }}
                <v-tooltip v-if="item.precioManual" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" x-small color="orange darken-2" class="ml-1">mdi-pencil</v-icon>
                  </template>
                  <span>{{ $t('common.precioManualTooltip') }}</span>
                </v-tooltip>
              </span>
            </template>
            <template v-slot:item.precioTotal="{ item }">
              <div class="font-weight-bold">
                ${{ getTotalLineaSinDescuentoGlobal(item).toFixed(2) }}
              </div>
            </template>
            <template v-slot:item.oferta="{ item }">
              <span v-if="tieneOfertaAplicada(item)" class="success--text font-weight-medium">
                <v-icon x-small>mdi-tag</v-icon> {{ getOfertaDescripcion(item) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template slot="no-data">
              <div class="text-center py-4 grey--text">Sin productos</div>
            </template>
          </v-data-table>
        </div>

      </v-card-text>

      <!-- ── ACCIONES ── -->
      <v-card-actions class="orden-show-actions flex-wrap align-center">
        <v-btn
          v-if="orden.estado === 'CONFIRMADO'"
          color="primary"
          class="mr-2 mb-2"
          :loading="loadingEstado"
          @click="abrirDialogEstado('ENVIADO')"
        >
          Marcar como Enviado
        </v-btn>
        <v-btn
          v-if="orden.estado === 'ENVIADO'"
          color="primary"
          class="mr-2 mb-2"
          :loading="loadingEstado"
          @click="abrirDialogEstado('RECIBIDO')"
        >
          Marcar como Recibido
        </v-btn>
        <v-btn
          v-if="mostrarBotonGenerarFactura"
          color="success"
          class="mr-2 mb-2"
          :loading="loadingFactura"
          @click="onGenerarFacturaClick"
        >
          Generar factura al cliente
        </v-btn>
        <v-btn
          v-if="puedeRegistrarPago"
          color="info"
          class="mr-2 mb-2"
          @click="showPagoModal = true"
        >
          Registrar Pago del Cliente
        </v-btn>
        <v-btn
          v-if="orden.estado === 'CONFIRMADO' || orden.estado === 'ENVIADO'"
          color="error"
          outlined
          class="mr-2 mb-2"
          :loading="loadingEstado"
          @click="abrirDialogEstado('CANCELADO')"
        >
          Cancelar orden
        </v-btn>
        <v-spacer />
        <div v-if="orden" class="footer-total">
          <div v-if="orden.descuento > 0" class="footer-total-sub">
            Desc. {{ orden.descuento }}%&nbsp;
            <span class="error--text">−${{ Number(orden.descuentoMonto || 0).toFixed(2) }}</span>
          </div>
          <div v-if="orden.recargo > 0" class="footer-total-sub">
            Rec. {{ orden.recargo }}%&nbsp;
            <span class="warning--text">+${{ Number(orden.recargoMonto || 0).toFixed(2) }}</span>
          </div>
          <div class="footer-total-amount">
            Total&nbsp;&nbsp;<strong>${{ Number(orden.precioTotal || 0).toFixed(2) }}</strong>
          </div>
        </div>
      </v-card-actions>
    </v-card>

    <v-card v-else>
      <v-card-text class="text-center">
        <v-progress-circular indeterminate color="primary" />
        <p class="mt-2">Cargando orden...</p>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogCambiarEstado.show" max-width="460">
      <v-card>
        <v-card-title>{{ dialogCambiarEstado.titulo }}</v-card-title>
        <v-card-text>{{ dialogCambiarEstado.mensaje }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCambiarEstado.show = false">Cancelar</v-btn>
          <v-btn
            :color="dialogCambiarEstado.nuevoEstado === 'CANCELADO' ? 'error' : 'primary'"
            :loading="loadingEstado"
            @click="confirmarCambioEstado"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <factura-emitir-modal
      v-model="showFacturaModal"
      :movimiento="movimientoParaFactura"
      :skip-choice-step="true"
      contexto-emision="ordenVenta"
      :documento-cliente="documentoClienteParaFactura"
      :orden-venta-id="ordenId"
      @factura-emitida="onFacturaEmitida"
    />

    <v-dialog v-model="showPagoModal" max-width="500" persistent>
      <v-card>
        <v-card-title>Registrar pago de la orden de venta</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <p class="mb-1">
              <strong>Total orden de venta:</strong>
              ${{ (orden && orden.precioTotal != null) ? Number(orden.precioTotal).toFixed(2) : '0.00' }}
            </p>
            <p class="mb-1" v-if="orden && (orden.totalPagado || 0) > 0">
              <strong>Ya pagado:</strong> ${{ Number(orden.totalPagado || 0).toFixed(2) }}
            </p>
            <p class="mb-2"><strong>Resto a pagar:</strong> ${{ restoAPagarFormatted }}</p>
          </div>

          <div class="mb-2 font-weight-medium">Medios de pago:</div>

          <div
            v-for="(item, index) in mediosDePago"
            :key="index"
            class="d-flex align-center mb-2"
            style="gap: 8px;"
          >
            <v-select
              v-model="item.medio"
              :items="mediosPagoOptions"
              outlined dense hide-details
              class="flex-grow-1"
            />
            <v-text-field
              v-model="item.monto"
              type="number"
              prefix="$"
              outlined dense hide-details
              class="flex-grow-1"
              style="max-width: 140px;"
              min="0" step="0.01"
            />
            <v-btn v-if="mediosDePago.length > 1" icon small color="error" @click="quitarMedioDePago(index)">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
          </div>

          <div class="d-flex align-center justify-space-between mt-1 mb-2">
            <v-btn small text color="primary" @click="agregarMedioDePago">
              <v-icon small left>mdi-plus</v-icon> Agregar medio de pago
            </v-btn>
            <v-btn v-if="mediosDePago.length === 1" small outlined color="primary" @click="pagarTotal">
              <v-icon small left>mdi-cash-check</v-icon> Pagar total
            </v-btn>
          </div>

          <v-alert v-if="pagoMontoError" type="error" dense outlined class="mt-2">
            {{ pagoMontoError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showPagoModal = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :disabled="!!pagoMontoError || (restoAPagar > 0 && sumaMediosDePago <= 0)"
            :loading="loadingPago"
            @click="registrarPago"
          >
            Registrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import OrdenVentaProvider from "../../providers/OrdenVentaProvider";
import FacturaEmitirModal from "../../../facturacion/components/FacturaEmitirModal";
import FacturacionProvider from "../../../facturacion/providers/FacturacionProvider";
import CompanyProvider from "../../../base/providers/CompanyProvider";
import { labelEstadoOrdenVenta, labelEstadoPago } from "../../utils/estadoLabels";
import { formatFechaVentas } from "../../utils/dateFormat";

export default {
  name: "OrdenVentaShow",
  components: { FacturaEmitirModal },
  props: {
    open: { type: Boolean, default: true },
    ordenId: { type: String, default: null },
  },
  data() {
    return {
      orden: null,
      loadingEstado: false,
      loadingPago: false,
      showFacturaModal: false,
      showPagoModal: false,
      dialogCambiarEstado: { show: false, nuevoEstado: null, titulo: '', mensaje: '' },
      loadingFactura: false,
      mediosDePago: [{ medio: "Efectivo", monto: 0 }],
      mediosPagoOptions: ["Efectivo", "Tarjeta Débito", "Tarjeta Crédito", "Transferencia"],
      afipActivo: null,
      facturaGenerada: false,
    };
  },
  computed: {
    headersItems() {
      return [
        { text: this.$t('producto.labels.nombre'), value: 'nombre', sortable: false },
        { text: this.$t('producto.labels.marca'), value: 'marca', sortable: false },
        { text: this.$t('producto.labels.modelo'), value: 'modelo', sortable: false },
        { text: this.$t('producto.labels.tamanio'), value: 'tamanio', sortable: false },
        { text: this.$t('producto.labels.cantidad'), value: 'cantidad', sortable: false },
        { text: this.$t('movimiento.labels.precioUnitario'), value: 'precioUnitario', sortable: false },
        { text: this.$t('movimiento.labels.oferta'), value: 'oferta', sortable: false },
        { text: this.$t('movimiento.labels.precioTotal'), value: 'precioTotal', sortable: false },
      ];
    },
    documentoClienteParaFactura() {
      if (!this.orden || !this.orden.cliente) return null;
      const c = this.orden.cliente;
      return { id: c.id, razonSocial: c.razonSocial, email: c.email || '' };
    },
    userCompanyId() {
      return this.$store.state.user && this.$store.state.user.companyId;
    },
    movimientoParaFactura() {
      if (!this.orden || !this.orden.movimientoId) return null;
      return { id: this.orden.movimientoId };
    },
    sumaMediosDePago() {
      return (this.mediosDePago || []).reduce((acc, p) => acc + (Number(p.monto) || 0), 0);
    },
    restoAPagar() {
      if (!this.orden) return 0;
      const rest = this.orden.restoAPagar;
      if (rest != null) return Number(rest);
      return Math.round((Number(this.orden.precioTotal || 0) - Number(this.orden.totalPagado || 0)) * 100) / 100;
    },
    restoAPagarFormatted() {
      return this.restoAPagar.toFixed(2);
    },
    pagoMontoError() {
      const total = this.sumaMediosDePago;
      if (total <= 0) return "";
      if (total > this.restoAPagar) return `No puede superar lo que falta por pagar ($${this.restoAPagarFormatted})`;
      return "";
    },
    puedeRegistrarPago() {
      if (!this.orden || this.orden.estadoPago === "PAGADO") return false;
      return this.orden.estado === "CONFIRMADO" || this.orden.estado === "ENVIADO" || this.orden.estado === "RECIBIDO";
    },
    mostrarBotonGenerarFactura() {
      return (
        this.orden &&
        this.orden.estado !== "CANCELADO" &&
        this.orden.items &&
        this.orden.items.length > 0 &&
        !this.facturaGenerada
      );
    },
  },
  mounted() {
    if (this.open && this.ordenId) this.load();
    this.verificarAfipActivo();
  },
  watch: {
    open(v) { if (v && this.ordenId) this.load(); else this.orden = null; },
    ordenId(v) { if (v && this.open) this.load(); },
    showPagoModal(v) { if (v) this.mediosDePago = [{ medio: "Efectivo", monto: 0 }]; },
  },
  methods: {
    formatFecha: formatFechaVentas,
    labelEstadoOrdenVenta,
    labelEstadoPago,
    vendedorDisplay(doc) {
      const v = doc.vendedor || doc.vendedorSnapshot;
      if (!v) return "—";
      return [v.nombre, v.apellido].filter(Boolean).join(" ") || "—";
    },
    estadoColor(estado) {
      return { CONFIRMADO: "blue", ENVIADO: "orange", RECIBIDO: "green", CANCELADO: "red" }[estado] || "grey";
    },
    getProductoNombre(item) {
      return item.productoNombre || `${item.productoMarca || ""} ${item.productoModelo || ""}`.trim();
    },
    getPrecioUnitarioOriginal(item) {
      if (item.precioBase != null && Number.isFinite(Number(item.precioBase))) return Number(item.precioBase);
      const cant = Number(item.cantidad) || 0;
      if (cant > 0 && item.precioBaseLinea != null) {
        return Math.round((Number(item.precioBaseLinea) / cant) * 100) / 100;
      }
      if (cant > 0 && item.precioConOfertas != null) {
        return Math.round((Number(item.precioConOfertas) / cant) * 100) / 100;
      }
      return item.precioUnitario != null ? Number(item.precioUnitario) : 0;
    },
    getTotalLineaSinDescuentoGlobal(item) {
      if (item.precioConOfertas != null) return Number(item.precioConOfertas) || 0;
      if (item.precioBaseLinea != null) return Number(item.precioBaseLinea) || 0;
      const cantidad = Number(item.cantidad) || 0;
      const precioBase = this.getPrecioUnitarioOriginal(item);
      if (cantidad > 0 && precioBase > 0) {
        return Math.round(cantidad * precioBase * 100) / 100;
      }
      return 0;
    },
    tieneOfertaAplicada(item) { return !!(item.ofertaSnapshot && item.ofertaSnapshot.nombre); },
    getOfertaDescripcion(item) {
      if (!this.tieneOfertaAplicada(item)) return "";
      const snap = item.ofertaSnapshot;
      const monto = snap && snap.descuentoAplicadoMonto;
      return monto != null ? `${snap.nombre} ($${Number(monto).toFixed(2)})` : snap.nombre;
    },
    async verificarAfipActivo() {
      if (!this.userCompanyId) { this.afipActivo = false; return; }
      try {
        const response = await CompanyProvider.companyAfipStatus();
        this.afipActivo = response.data && response.data.companyAfipStatus && response.data.companyAfipStatus.afipActivo;
      } catch { this.afipActivo = false; }
    },
    async load() {
      if (!this.ordenId) return;
      try {
        const r = await OrdenVentaProvider.findOrdenVenta(this.ordenId);
        this.orden = (r.data && r.data.ordenVentaFind) || null;
        await this.actualizarEstadoFacturaGenerada();
      } catch (e) { console.error(e); }
    },
    async actualizarEstadoFacturaGenerada() {
      this.facturaGenerada = false;
      if (this.orden && (this.orden.estadoFacturacion === "FACTURADA" || this.orden.facturaId)) {
        this.facturaGenerada = true;
        return;
      }
      if (!this.orden || !this.orden.movimientoId) return;
      try {
        const r = await FacturacionProvider.getFacturaExistePorMovimiento(this.orden.movimientoId);
        this.facturaGenerada = !!(r.data && r.data.facturaExistePorMovimiento);
      } catch { /* silencioso */ }
    },
    abrirDialogEstado(nuevoEstado) {
      const configs = {
        ENVIADO: { titulo: 'Marcar como Enviado', mensaje: '¿Marcar la orden de venta como Enviada?' },
        RECIBIDO: { titulo: 'Marcar como Recibido', mensaje: '¿Marcar la orden de venta como Recibida?' },
        CANCELADO: { titulo: 'Cancelar orden', mensaje: '¿Cancelar la orden de venta? Esta acción no se puede deshacer.' },
      }
      const cfg = configs[nuevoEstado] || { titulo: 'Confirmar acción', mensaje: '¿Confirmar?' }
      this.dialogCambiarEstado = { show: true, nuevoEstado, ...cfg }
    },
    async confirmarCambioEstado() {
      await this.cambiarEstado(this.dialogCambiarEstado.nuevoEstado)
      this.dialogCambiarEstado.show = false
    },
    async cambiarEstado(estado) {
      if (!this.ordenId) return;
      this.loadingEstado = true;
      try { await OrdenVentaProvider.cambiarEstado(this.ordenId, estado); await this.load(); this.$emit("refreshed"); }
      catch (e) { console.error(e); }
      finally { this.loadingEstado = false; }
    },
    async onGenerarFacturaClick() {
      if (!this.orden || !this.ordenId) return;
      this.showFacturaModal = true;
    },
    onFacturaEmitida() { this.showFacturaModal = false; this.facturaGenerada = true; this.$emit("refreshed"); },
    agregarMedioDePago() { this.mediosDePago.push({ medio: "Efectivo", monto: 0 }); },
    quitarMedioDePago(index) { if (this.mediosDePago.length > 1) this.mediosDePago.splice(index, 1); },
    pagarTotal() {
      const medio = (this.mediosDePago[0] && this.mediosDePago[0].medio) || "Efectivo";
      this.mediosDePago = [{ medio, monto: this.restoAPagar }];
    },
    async registrarPago() {
      if (!this.ordenId) return;
      if (this.restoAPagar <= 0) {
        this.loadingPago = true;
        try {
          const medio = (this.mediosDePago[0] && this.mediosDePago[0].medio) || "Efectivo";
          await OrdenVentaProvider.registrarPago(this.ordenId, 0, medio);
          this.showPagoModal = false;
          this.mediosDePago = [{ medio: "Efectivo", monto: 0 }];
          await this.load();
          this.$emit("refreshed");
        } catch (e) { console.error(e); }
        finally { this.loadingPago = false; }
        return;
      }
      const pagos = (this.mediosDePago || []).filter((p) => Number(p.monto) > 0);
      if (!pagos.length) return;
      const total = pagos.reduce((acc, p) => acc + (Number(p.monto) || 0), 0);
      if (total > this.restoAPagar) return;
      this.loadingPago = true;
      try {
        for (const pago of pagos) {
          await OrdenVentaProvider.registrarPago(this.ordenId, Number(pago.monto), pago.medio || "Efectivo");
        }
        this.showPagoModal = false;
        this.mediosDePago = [{ medio: "Efectivo", monto: 0 }];
        await this.load();
        this.$emit("refreshed");
      } catch (e) { console.error(e); }
      finally { this.loadingPago = false; }
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
.movimiento-show-table {
  border-radius: 8px;
  overflow: hidden;
}
::v-deep .orden-show-dialog-content {
  height: 90vh;
  max-height: 90vh;
}
.orden-show-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.orden-show-content {
  flex: 1 1 auto;
  overflow-y: auto;
}
.orden-show-actions {
  flex: 0 0 auto;
  border-top: 1px solid #e0e0e0;
}

/* Info panel */
.show-info-panel {
  background: #f8f9fc;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  padding: 12px 16px;
}
.info-field { padding: 2px 0; }
.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #90a4ae;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.info-value {
  font-size: 0.92rem;
  font-weight: 500;
  color: #37474f;
  margin-top: 2px;
}

::v-deep .v-chip { color: #fff !important; }
::v-deep .v-chip .v-chip__content { color: #fff !important; }

.footer-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
}
.footer-total-sub {
  font-size: 0.85rem;
  color: #90a4ae;
}
.footer-total-amount {
  font-size: 1.25rem;
  color: #1565c0;
  font-weight: 600;
}
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  padding: 3px 0;
  color: #546e7a;
}
.total-label { color: #90a4ae; }
.total-final {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1565c0;
  padding-top: 2px;
}
</style>
